type MeshCommitTask<T> = {
  key: string;
  priority(): number;
  shouldRun(): boolean;
  task(): T;
  abortController: AbortController;
  // Bytes the commit will upload to the GPU on the next render.
  uploadBytes?: number;
};

type QueuedMeshCommit<T> = MeshCommitTask<T> & {
  resolve(value: T): void;
  reject(reason: Error): void;
  abortListener(): void;
};

export type MeshCommitFrameBudget = {
  maxCommits: number;
  maxUploadBytes: number;
  maxElapsedMs: number;
};

const BYTES_PER_MEBIBYTE = 1_048_576;
const DEFAULT_MAX_UPLOAD_MEBIBYTES_PER_FRAME = 2.5;

// A heavy (16k-vertex) tile uploads ~1 MB of geometry and texture, so this
// allows two of them, or several light tiles, per frame. Uploads happen on
// the next render, so the byte budget is what bounds that frame.
export const DEFAULT_MESH_COMMIT_FRAME_BUDGET: MeshCommitFrameBudget = {
  maxCommits: 4,
  maxUploadBytes: DEFAULT_MAX_UPLOAD_MEBIBYTES_PER_FRAME * BYTES_PER_MEBIBYTE,
  maxElapsedMs: 4,
};

export class MeshCommitQueue<T> {
  private queue: Array<QueuedMeshCommit<T>> = [];
  private frameHandle: number | undefined;
  private disposed = false;

  constructor(
    private readonly frameBudget: MeshCommitFrameBudget = DEFAULT_MESH_COMMIT_FRAME_BUDGET
  ) {}

  public add(task: MeshCommitTask<T>): Promise<T> {
    if (this.disposed || task.abortController.signal.aborted) {
      return Promise.reject(this.createAbortError(task.key));
    }

    return new Promise<T>((resolve, reject) => {
      const abortListener = (): void => {
        const taskIndex = this.queue.findIndex(
          (queuedTask) => queuedTask.key === task.key
        );
        if (taskIndex === -1) {
          return;
        }

        const [abortedTask] = this.queue.splice(taskIndex, 1);
        abortedTask.abortController.signal.removeEventListener(
          'abort',
          abortedTask.abortListener
        );
        abortedTask.reject(this.createAbortError(abortedTask.key));
      };
      const queuedTask: QueuedMeshCommit<T> = {
        ...task,
        resolve,
        reject,
        abortListener,
      };

      task.abortController.signal.addEventListener('abort', abortListener, {
        once: true,
      });
      this.queue.push(queuedTask);
      this.scheduleFrame();
    });
  }

  public dispose(): void {
    this.disposed = true;
    if (this.frameHandle !== undefined) {
      cancelAnimationFrame(this.frameHandle);
      this.frameHandle = undefined;
    }

    const tasks = this.queue;
    this.queue = [];
    tasks.forEach((task) => {
      task.abortController.signal.removeEventListener(
        'abort',
        task.abortListener
      );
      task.reject(this.createAbortError(task.key));
    });
  }

  private scheduleFrame(): void {
    if (
      this.disposed ||
      this.frameHandle !== undefined ||
      this.queue.length === 0
    ) {
      return;
    }

    this.frameHandle = requestAnimationFrame(() => {
      this.frameHandle = undefined;
      this.processFrame();
      this.scheduleFrame();
    });
  }

  // Always commits at least one task. Each further commit must fit the
  // remaining count and upload budget before it runs; the elapsed-time check
  // only guards against commits being slower than expected.
  private processFrame(): void {
    const frameStart = performance.now();
    const { maxCommits, maxUploadBytes, maxElapsedMs } = this.frameBudget;
    let commits = 0;
    let uploadBytes = 0;

    this.sortQueueByPriority();
    while (this.queue.length > 0 && !this.disposed) {
      const nextUploadBytes = this.queue[0].uploadBytes ?? 0;
      if (
        commits > 0 &&
        (commits >= maxCommits ||
          uploadBytes + nextUploadBytes > maxUploadBytes ||
          performance.now() - frameStart >= maxElapsedMs)
      ) {
        return;
      }

      if (this.processNext()) {
        commits++;
        uploadBytes += nextUploadBytes;
      }
    }
  }

  private sortQueueByPriority(): void {
    const ranked = this.queue.map((task) => ({
      task,
      priority: task.priority(),
    }));
    ranked.sort((a, b) => a.priority - b.priority);
    this.queue = ranked.map(({ task }) => task);
  }

  // Returns whether the task ran; skipped tasks use none of the budget.
  private processNext(): boolean {
    const task = this.queue.shift();
    if (!task) {
      return false;
    }

    task.abortController.signal.removeEventListener(
      'abort',
      task.abortListener
    );
    if (task.abortController.signal.aborted || !task.shouldRun()) {
      task.reject(this.createAbortError(task.key));
      return false;
    }

    try {
      task.resolve(task.task());
    } catch (error) {
      task.reject(error as Error);
    }
    return true;
  }

  private createAbortError(key: string): Error {
    const error = new Error(`Mesh commit aborted for task: ${key}`);
    error.name = 'AbortError';
    return error;
  }
}
